import { Component, inject, OnInit, signal } from "@angular/core";
import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NumbersOnlyDirective } from "../../../../../../shared/src/lib/directives/numbers-only.directive";
import { BalanceService } from "../../../../../../shared/src/lib/services/balance.service";
import { LoaderService } from "../../../../../../shared/src/lib/services/loader.service";
import { MathUtils } from "../../../../../../shared/src";
import { Observable, combineLatest, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { MatProgressBar } from "@angular/material/progress-bar";

const EXCHANGE = "main_to_secondary";
const CASINO = "secondary_to_main";

@Component({
    selector: "app-wallet-transfer",
    standalone: true,
    imports: [DecimalPipe, MatIconModule, ReactiveFormsModule, NumbersOnlyDirective, AsyncPipe, NgClass, MatProgressBar],
    templateUrl: "./wallet-transfer.component.html",
    styleUrl: "./wallet-transfer.component.scss",
})
export class WalletTransferComponent implements OnInit {
    readonly destroy$ = new Subject<void>();
    balanceService = inject(BalanceService);
    formBuilder = inject(FormBuilder);
    loader = inject(LoaderService);

    form!: FormGroup;
    isLoading$!: Observable<boolean>;
    exchangeBalance = 0;
    casinoBalance = 0;
    noticeTimeout: any;
    transferNotice = signal<"success" | "error" | null>(null);

    get isExchange() {
        return this.transferTypeCtrl?.value === EXCHANGE;
    }

    get fromDescription() {
        return this.isExchange ? "Exchange" : "Casino";
    }

    get toDescription() {
        return this.isExchange ? "Casino" : "Exchange";
    }

    get isBalanceInsufficient() {
        const amount = this.selectedAmount;
        return amount > this.fromBalance;
    }

    get fromBalance() {
        return this.isExchange ? this.exchangeBalance : this.casinoBalance;
    }

    get toBalance() {
        return this.isExchange ? this.casinoBalance : this.exchangeBalance;
    }

    get selectedAmount() {
        return Number(this.amountCtrl?.value);
    }

    get amountCtrl() {
        return this.form.get("AMOUNT");
    }

    get transferTypeCtrl() {
        return this.form.get("TRANSFER_TYPE");
    }

    get isTransferInvalid() {
        return this.form.invalid || this.isBalanceInsufficient || this.selectedAmount <= 0;
    }

    constructor() {
        this.form = this.formBuilder.group({
            AMOUNT: [null, [Validators.required, Validators.min(1)]],
            TRANSFER_TYPE: EXCHANGE,
        });
    }

    ngOnInit() {
        this.balanceService.fetchBalance();

        this.isLoading$ = combineLatest([this.loader.isLoading("/user/wallets"), this.loader.isLoading("/internal-transfer")]).pipe(
            takeUntil(this.destroy$),
            map(([fetchingBalance, transferringBalance]) => fetchingBalance || transferringBalance),
        );

        this.balanceService.balanceData$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result) {
                const balance = MathUtils.roundDown(+result?.BALANCE, 2);
                const totalExposure = +result?.TOTAL_EXPOSURE;
                this.exchangeBalance = MathUtils.roundDown(balance + totalExposure, 2);
            }
        });

        this.balanceService.casinoBalance$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result?.BALANCE) this.casinoBalance = Number(result?.BALANCE);
        });

        this.transferTypeCtrl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            // Validate amount after swapping
            if (this.amountCtrl?.value && this.selectedAmount > this.fromBalance) {
                this.form.patchValue({ AMOUNT: this.fromBalance }, { emitEvent: false });
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    swapWallets() {
        const newValue = this.isExchange ? CASINO : EXCHANGE;
        this.form.patchValue({ TRANSFER_TYPE: newValue });
    }

    onFromWalletChange(event: Event) {
        const target = event.target as HTMLSelectElement;
        this.form.patchValue({ TRANSFER_TYPE: target.value });
    }

    onToWalletChange(event: Event) {
        const target = event.target as HTMLSelectElement;
        const newValue = target.value === EXCHANGE ? CASINO : EXCHANGE;
        this.form.patchValue({ TRANSFER_TYPE: newValue });
    }

    setMax() {
        this.form.patchValue({ AMOUNT: this.fromBalance });
        this.amountCtrl?.markAsTouched();
        this.amountCtrl?.markAsDirty();
    }

    adjustAmount(type: "increase" | "decrease") {
        const amount = this.selectedAmount || 0;
        const newAmount = type === "increase" ? amount + 1 : amount - 1;
        if (newAmount > 0 && newAmount <= this.fromBalance) {
            this.form.patchValue({ AMOUNT: newAmount });
            this.amountCtrl?.markAsTouched();
            this.amountCtrl?.markAsDirty();
        }
    }

    handleSubmit() {
        if (this.isTransferInvalid) return;

        this.balanceService
            .transferBalance(this.form.value)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.transferNotice.set(response?.status === "ok" ? "success" : "error");
                    this.balanceService.fetchBalance();
                    this.resetNotice();
                },
                error: () => {
                    this.transferNotice.set("error");
                    this.resetNotice();
                },
            });
    }

    resetNotice() {
        this.noticeTimeout = setTimeout(() => {
            this.transferNotice.set(null);
            this.resetForm();
        }, 5000);
    }

    closeNotice() {
        clearTimeout(this.noticeTimeout);
        this.transferNotice.set(null);
        this.resetForm();
    }

    resetForm() {
        this.form.patchValue({ AMOUNT: null });
        this.amountCtrl?.markAsUntouched();
        this.amountCtrl?.markAsPristine();
    }
}
